import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MindMapContext } from '../../Context/contex';
import { isEditingText } from '../UndoRedo/keyboard';
import CollapsedStrip from './CollapsedStrip';
import ShapePanel from './ShapePanel';
import { CATEGORIES, SHAPE_COLOR_DRAG_TYPE, SHAPE_DRAG_TYPE, searchShapes } from './shapeLibrary';
import { useCollapsed, useRecentShapes } from './sidebarStorage';
import './sidebar.css';

// The shape library (D-038): search, category filters, recently used shapes and a collapsed strip.
// Dragging a shape onto the mind map adds a node of its type in its category's colour (MindMapContainer `onDrop`).
const Sidebar = () => {
    const { isReadOnly } = MindMapContext();
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('all');
    const [collapsed, setCollapsed] = useCollapsed();
    const [recent, rememberShape] = useRecentShapes();
    const inputRef = useRef(null);
    const pendingFocus = useRef(false);

    const dragProps = useCallback((shape) => ({
        draggable: true,
        onDragStart: (event) => {
            event.dataTransfer.setData(SHAPE_DRAG_TYPE, shape.type);
            event.dataTransfer.setData(SHAPE_COLOR_DRAG_TYPE, CATEGORIES[shape.category].nodeColor);
            event.dataTransfer.effectAllowed = 'move';
            rememberShape(shape.name);
        },
    }), [rememberShape]);

    const focusSearch = useCallback(() => {
        pendingFocus.current = true;
        setCollapsed(false);
        inputRef.current?.focus();
    }, [setCollapsed]);

    // After expanding from the strip, the search box exists only on the next render.
    useEffect(() => {
        if (!collapsed && pendingFocus.current) {
            pendingFocus.current = false;
            inputRef.current?.focus();
        }
    }, [collapsed]);

    // "/" jumps to the search box, unless the user is typing somewhere.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key !== '/' || event.ctrlKey || event.metaKey || isEditingText(event.target)) return;
            event.preventDefault();
            focusSearch();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [focusSearch]);

    const reset = () => {
        setQuery('');
        setCategory('all');
    };

    return (
        <div className={`sidebar ${collapsed ? 'is-collapsed' : ''}`.trim()} style={{ pointerEvents: isReadOnly ? 'none' : 'all' }}>
            {collapsed ? (
                <CollapsedStrip recent={recent} dragProps={dragProps} onExpand={() => setCollapsed(false)} onSearch={focusSearch} />
            ) : (
                <ShapePanel
                    inputRef={inputRef}
                    query={query}
                    onQuery={setQuery}
                    category={category}
                    onCategory={setCategory}
                    onCollapse={() => setCollapsed(true)}
                    onReset={reset}
                    results={searchShapes(query, category)}
                    recent={recent}
                    dragProps={dragProps}
                />
            )}
        </div>
    );
};

export default Sidebar;
